import React, { useState, useEffect, useCallback, useRef } from 'react';
import { productAPI, categoryAPI, techniqueAPI, mediaAPI, invoiceAPI, clientAPI, sizeAPI } from '../../services/api';
import { Button, Modal, FormField, Input, Textarea, Select, ConfirmDialog, EmptyState, Pagination } from '../../components/common';
import { toast } from 'react-toastify';

/* ════════════════════════════════════════════════════
   MÉDIATHÈQUE — avec upload, copie de lien, suppression
   ════════════════════════════════════════════════════ */
export const AdminMediaPage = () => {
  const [medias,      setMedias]      = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [folder,      setFolder]      = useState('');
  const [uploading,   setUploading]   = useState(false);
  const [preview,     setPreview]     = useState(null);
  const [confirm,     setConfirm]     = useState(null);
  const [search,      setSearch]      = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [unusedOnly,  setUnusedOnly]  = useState(false);
  const [loadingUnused, setLoadingUnused] = useState(false);
  const fileRef = useRef();

  const folders = ['general','products','mockups','blog','logos','backgrounds'];

  const load = useCallback(() => {
    setLoading(true);
    mediaAPI.list(folder ? { folder } : {})
      .then(({ data }) => setMedias(data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [folder]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    let ok = 0;
    const errors = [];
    for (const file of files) {
      // Validation côté client avant envoi
      if (file.size > 10 * 1024 * 1024) {
        errors.push(`${file.name} : trop volumineux (max 10 Mo)`);
        continue;
      }
      if (!file.type.startsWith('image/')) {
        errors.push(`${file.name} : type non supporté`);
        continue;
      }
      const fd = new FormData();
      fd.append('file', file);
      fd.append('folder', folder || 'general');
      try {
        await mediaAPI.upload(fd);
        ok++;
      } catch (err) {
        const msg = err.response?.data?.message || err.message || 'Erreur inconnue';
        errors.push(`${file.name} : ${msg}`);
      }
    }
    if (ok > 0) toast.success(`✓ ${ok} image(s) uploadée(s)`);
    if (errors.length > 0) {
      errors.forEach(e => toast.error(e, { autoClose: 6000 }));
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
    load();
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Lien copié !');
  };

  const del = async (id) => {
    try { await mediaAPI.delete(id); toast.success('Supprimé'); load(); }
    catch { toast.error('Erreur suppression'); }
    setConfirm(null);
  };

  const toggleSelect = (id) => setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const isAllSelected = filtered => filtered.length > 0 && filtered.every(m => selectedIds.has(m.id));
  const toggleAll = (list) => setSelectedIds(isAllSelected(list) ? new Set() : new Set(list.map(m => m.id)));

  const bulkDelete = async () => {
    try {
      await mediaAPI.bulkDelete([...selectedIds]);
      toast.success(`${selectedIds.size} fichier(s) supprimé(s)`);
      setSelectedIds(new Set());
      setBulkConfirm(false);
      load();
    } catch { toast.error('Erreur lors de la suppression'); }
  };

  const loadUnused = async () => {
    if (unusedOnly) { setUnusedOnly(false); load(); return; }
    setLoadingUnused(true);
    try {
      const { data } = await mediaAPI.unused();
      setMedias(data.data || []);
      setUnusedOnly(true);
      toast.info(`${(data.data||[]).length} image(s) non utilisée(s) trouvée(s)`);
    } catch { toast.error('Erreur'); }
    finally { setLoadingUnused(false); }
  };

  const filtered = medias.filter(m =>
    !search || m.original_name.toLowerCase().includes(search.toLowerCase())
  );

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' o';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' Ko';
    return (bytes / 1024 / 1024).toFixed(1) + ' Mo';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      {/* Header */}
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Médiathèque</h1>
          <p className="admin-page-subtitle">{medias.length} fichier(s){unusedOnly && <span style={{ color:'var(--warning)', fontWeight:700 }}> — filtre : non utilisées</span>}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap:'wrap' }}>
          <Button variant={unusedOnly ? 'primary' : 'secondary'} loading={loadingUnused} onClick={loadUnused}
            title="Affiche uniquement les images non référencées dans les produits, blog, maquettes ou paramètres">
            {unusedOnly ? '✕ Tout afficher' : '🔍 Non utilisées'}
          </Button>
          <label className="btn btn-primary btn-md" style={{ cursor: 'pointer' }}>
            {uploading ? <><span className="btn-spinner" /> Envoi…</> : 'Uploader des images'}
            <input ref={fileRef} type="file" multiple accept="image/*" onChange={handleUpload} hidden />
          </label>
        </div>
      </div>

      <div className="admin-page-content">

        {/* Zone de drop */}
        <div
          onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = 'var(--brand-accent)'; e.currentTarget.style.background = 'rgba(230,57,70,.04)'; }}
          onDragLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--bg-page)'; }}
          onDrop={async e => {
            e.preventDefault();
            e.currentTarget.style.borderColor = 'var(--border)';
            e.currentTarget.style.background = 'var(--bg-page)';
            const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
            if (!files.length) { toast.error('Glissez uniquement des images'); return; }
            // Simuler l'événement
            const dt = new DataTransfer();
            files.forEach(f => dt.items.add(f));
            const fakeEvent = { target: { files: dt.files } };
            handleUpload(fakeEvent);
          }}
          style={{ border: '2px dashed var(--border)', borderRadius: 'var(--radius-lg)', padding: '20px', textAlign: 'center', background: 'var(--bg-page)', marginBottom: 'var(--space-md)', transition: 'all .2s', cursor: 'pointer' }}
          onClick={() => fileRef.current?.click()}
        >
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>Glissez des images ici ou cliquez pour sélectionner</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>JPG, PNG, WebP, GIF, SVG — max 10 Mo — dossier : <strong>{folder || 'general'}</strong></div>
        </div>

        {/* Filtres */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
          <button
            className={`btn btn-sm ${!folder ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setFolder('')}
          >Tout</button>
          {folders.map(f => (
            <button
              key={f}
              className={`btn btn-sm ${folder === f ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setFolder(f)}
            >{f}</button>
          ))}
          <div className="search-bar" style={{ marginLeft: 'auto' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input className="form-input" placeholder="Rechercher…" value={search} onChange={e => setSearch(e.target.value)} style={{ paddingLeft: 34, width: 200 }} />
          </div>
        </div>

        {loading ? <div className="page-loader"><div className="spinner spinner-lg" /></div> :
          !filtered.length ? <EmptyState icon="🖼" title={unusedOnly ? 'Aucune image non utilisée ✓' : 'Aucun fichier'} description={unusedOnly ? 'Toutes vos images sont utilisées quelque part.' : 'Uploadez vos premières images.'} /> : (
            <>
              {/* Barre de sélection multiple */}
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10, flexWrap:'wrap' }}>
                <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, cursor:'pointer', userSelect:'none' }}>
                  <input type="checkbox" checked={isAllSelected(filtered)} onChange={() => toggleAll(filtered)} />
                  {isAllSelected(filtered) ? 'Tout désélectionner' : `Tout sélectionner (${filtered.length})`}
                </label>
                {selectedIds.size > 0 && (
                  <>
                    <span style={{ fontSize:13, fontWeight:600, color:'var(--brand-accent)' }}>{selectedIds.size} sélectionné(s)</span>
                    <Button size="sm" variant="ghost" onClick={() => setBulkConfirm(true)} style={{ color:'var(--error)' }}>
                      🗑 Supprimer ({selectedIds.size})
                    </Button>
                    <button onClick={() => setSelectedIds(new Set())} style={{ fontSize:12, color:'var(--text-muted)', background:'none', border:'none', cursor:'pointer' }}>
                      Désélectionner
                    </button>
                  </>
                )}
              </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
              {filtered.map(m => (
                <div key={m.id} style={{ background: 'var(--bg-card)', border: `1.5px solid ${selectedIds.has(m.id) ? 'var(--brand-accent)' : 'var(--border)'}`, borderRadius: 'var(--radius-lg)', overflow: 'hidden', display: 'flex', flexDirection: 'column', transition: 'border-color .15s' }}>
                  {/* Image + checkbox */}
                  <div
                    style={{ aspectRatio: '4/3', background: 'var(--bg-page)', overflow: 'hidden', cursor: 'pointer', position: 'relative' }}
                  >
                    <img src={m.url} alt={m.original_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onClick={() => setPreview(m)} />
                    {/* Checkbox overlay */}
                    <div style={{ position:'absolute', top:6, left:6, zIndex:2 }}>
                      <input type="checkbox" checked={selectedIds.has(m.id)} onChange={() => toggleSelect(m.id)}
                        style={{ width:16, height:16, cursor:'pointer', accentColor:'var(--brand-accent)' }}
                        onClick={e => e.stopPropagation()} />
                    </div>
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity .2s' }}
                      onMouseEnter={e => e.currentTarget.style.opacity = 1}
                      onMouseLeave={e => e.currentTarget.style.opacity = 0}
                      onClick={() => setPreview(m)}
                    >
                      <span style={{ color: 'white', fontSize: 13, fontWeight: 600 }}>Aperçu</span>
                    </div>
                  </div>

                  {/* Info */}
                  <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={m.original_name}>
                      {m.original_name}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', justifyContent: 'space-between' }}>
                      <span>{formatSize(m.size)}</span>
                      <span style={{ background: 'var(--bg-page)', padding: '1px 6px', borderRadius: 4, border: '1px solid var(--border)' }}>{m.folder}</span>
                    </div>

                    {/* Lien URL copiable */}
                    <div
                      onClick={() => copyLink(m.url)}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px', background: 'var(--bg-page)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', transition: 'all .2s' }}
                      title="Cliquer pour copier le lien"
                      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--brand-accent)'; e.currentTarget.style.background = 'rgba(230,57,70,.04)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--bg-page)'; }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--brand-accent)" strokeWidth="2"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
                      <span style={{ fontSize: 10, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{m.url}</span>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: 6, marginTop: 'auto' }}>
                      <Button size="sm" variant="secondary" style={{ flex: 1 }} onClick={() => copyLink(m.url)}>
                        📋 Copier le lien
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setConfirm(m.id)}>🗑</Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            </>
          )
        }
      </div>

      {/* Modal aperçu */}
      <Modal isOpen={!!preview} onClose={() => setPreview(null)} title={preview?.original_name || ''} size="lg">
        {preview && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <img src={preview.url} alt={preview.original_name} style={{ width: '100%', maxHeight: 500, objectFit: 'contain', borderRadius: 'var(--radius-md)', background: 'var(--bg-page)' }} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 13 }}>
              <div><span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Dossier :</span> {preview.folder}</div>
              <div><span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Taille :</span> {formatSize(preview.size)}</div>
              <div><span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Type :</span> {preview.mime_type}</div>
              <div><span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Ajouté le :</span> {new Date(preview.created_at).toLocaleDateString('fr-FR')}</div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="primary" onClick={() => copyLink(preview.url)} style={{ flex: 1 }}>
                📋 Copier le lien
              </Button>
              <Button variant="secondary" as="a" href={preview.url} target="_blank" rel="noreferrer">
                ↗ Ouvrir
              </Button>
            </div>
            <div style={{ padding: '10px 12px', background: 'var(--bg-page)', borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font-mono)', fontSize: 11, wordBreak: 'break-all', color: 'var(--text-secondary)', cursor: 'pointer' }} onClick={() => copyLink(preview.url)}>
              {preview.url}
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog isOpen={!!confirm} onClose={() => setConfirm(null)} onConfirm={() => del(confirm)} title="Supprimer le fichier" message="Cette action est irréversible. Le fichier sera supprimé du serveur." confirmLabel="Supprimer" danger />
      <ConfirmDialog isOpen={bulkConfirm} onClose={() => setBulkConfirm(false)} onConfirm={bulkDelete} title="Supprimer la sélection" message={`${selectedIds.size} fichier(s) seront définitivement supprimés du serveur.`} confirmLabel="Supprimer tout" danger />
    </div>
  );
};


/* ════════════════════════════════════════════════════
   PRODUITS AMÉLIORÉS — gestion images + image principale
   ════════════════════════════════════════════════════ */
export const AdminProductsEnhancedPage = () => {
  const [products,   setProducts]   = useState([]);
  const [categories, setCategories] = useState([]);
  const [catError,    setCatError]    = useState('');
  const [techniques, setTechniques] = useState([]);
  const [medias,     setMedias]     = useState([]);
  const [meta,       setMeta]       = useState(null);
  const [page,       setPage]       = useState(1);
  const [loading,    setLoading]    = useState(true);
  const [editModal,  setEditModal]  = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [confirm,     setConfirm]    = useState(null);
  const [search,      setSearch]     = useState('');
  const [filterPub,   setFilterPub]  = useState('');
  const [filterCat,    setFilterCat]   = useState('');
  const [filterFeat,   setFilterFeat]  = useState('');
  const [filterGender, setFilterGender]= useState('');
  const [sortBy,       setSortBy]      = useState('newest');
  const [activeTab,   setActiveTab]  = useState('info');
  const [selectedIds, setSelectedIds]= useState(new Set());
  const [bulkConfirm, setBulkConfirm]= useState(false);
  const fileRef       = useRef();
  const importRef     = useRef();
  const [uploading,   setUploading]   = useState(false);
  const [importModal, setImportModal] = useState(false);
  const [importData,  setImportData]  = useState([]);
  const [importing,   setImporting]   = useState(false);
  const [importErrors,   setImportErrors]   = useState([]);
  const [applyingPS,     setApplyingPS]     = useState(false);
  const [previewProd,    setPreviewProd]    = useState(null);

  const emptyForm = {
    name:'', reference:'', category_id:'', description:'', short_desc:'',
    base_price:'', faces_count:2, is_published:false, is_featured:false,
    brand:'', material:'', weight:'', gender:'', packshot_code:'',
    available_sizes:[],
    available_colors:[], technique_ids:[], sort_order:0,
    main_image:'', meta_title:'', meta_description:'', product_images:[]
  };
  const [form, setForm]   = useState(emptyForm);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Génère le style CSS d'un cercle uni ou divisé selon le nombre de hexes
  const splitCircle = (hexes) => {
    const h = hexes?.length > 0 ? hexes : ['#000000'];
    if (h.length === 1) return { background: h[0] };
    const step = 360 / h.length;
    const stops = h.map((c, i) => `${c} ${i * step}deg ${(i + 1) * step}deg`).join(', ');
    return { background: `conic-gradient(${stops})` };
  };

  // Normalise les couleurs : accepte strings, {name,hex} ou {name,hex,hexes:[]}
  const normalizeColors = (colors) => {
    if (!Array.isArray(colors)) {
      try { colors = JSON.parse(colors || '[]'); } catch { return []; }
    }
    return colors.map(c => {
      if (typeof c === 'string') return { name: c, hex: '#000000', hexes: ['#000000'] };
      const hexes = (c.hexes?.length > 0 ? c.hexes : [c.hex || '#000000']);
      return { ...c, hex: hexes[0], hexes };
    }).filter(c => c.name);
  };

  // Tailles dynamiques — uniquement depuis la DB, aucune valeur statique
  const [availableSizes, setAvailableSizes] = useState([]);
  const [sizesLoaded,    setSizesLoaded]    = useState(false);
  useEffect(() => {
    sizeAPI.list().then(({ data }) => {
      setAvailableSizes((data.data || []).map(s => s.name));
    }).catch(() => {}).finally(() => setSizesLoaded(true));
  }, []);

  // Couleurs
  const [newColor, setNewColor] = useState({ name: '', hexes: ['#000000'] });

  const SORT_MAP = {
    newest:    { sort: 'created_at', order: 'desc' },
    oldest:    { sort: 'created_at', order: 'asc'  },
    name_az:   { sort: 'name',       order: 'asc'  },
    name_za:   { sort: 'name',       order: 'desc' },
    price_asc: { sort: 'base_price', order: 'asc'  },
    price_desc:{ sort: 'base_price', order: 'desc' },
  };

  const hasFilters = filterPub !== '' || filterCat !== '' || filterFeat !== '' || filterGender !== '' || sortBy !== 'newest';

  const resetFilters = () => { setFilterPub(''); setFilterCat(''); setFilterFeat(''); setFilterGender(''); setSortBy('newest'); setSearch(''); setPage(1); };

  const load = useCallback(() => {
    setLoading(true);
    setCatError('');
    const params = { page, per_page: 16, ...SORT_MAP[sortBy] };
    if (search)        params.search    = search;
    if (filterPub !== '')  params.published = parseInt(filterPub);
    if (filterCat)         params.category  = filterCat;
    if (filterFeat !== '') params.featured  = parseInt(filterFeat);
    if (filterGender)      params.gender   = filterGender;

    // Chargements critiques séparés du chargement optionnel des médias
    Promise.all([
      productAPI.list(params),
      categoryAPI.list(),
      techniqueAPI.listAll(),
    ]).then(([p, c, t]) => {
      setProducts(p.data.data || []);
      setMeta(p.data.meta);
      setCategories(c.data.data || []);
      setTechniques(t.data.data || []);
    }).catch(err => {
      const msg = err.response?.data?.message || err.message || 'Erreur réseau';
      setCatError(msg);
      console.error('[Categories]', msg);
    }).finally(() => setLoading(false));

    // Médias chargés séparément — leur échec ne bloque pas le formulaire
    mediaAPI.list({ folder: 'products' })
      .then(({ data }) => setMedias(data.data || []))
      .catch(() => setMedias([]));
  }, [page, search, filterPub, filterCat, filterFeat, filterGender, sortBy]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  /* ── Import CSV produits ── */
  // Supprime les accents : "Catégorie" → "categorie"
  const removeAccents = (s) => s.normalize('NFD').replace(/[̀-ͯ]/g,'');

  // Normalise un header CSV : BOM, guillemets, casse, accents, espaces multiples
  const normHeader = (h) =>
    removeAccents(
      h.replace(/﻿/g,'').trim().replace(/^"|"$/g,'').toLowerCase()
       .replace(/[_\-\.]+/g,' ').replace(/\s+/g,' ')
    ).trim();

  // Toutes les clés sont déjà sans accents (removeAccents appliqué à l'entrée)
  const PROD_HEADER_MAP = {
    // Nom
    'nom':'name','name':'name','produit':'name','libelle':'name','designation':'name',
    // Référence
    'reference':'reference','ref':'reference','sku':'reference','code':'reference','code article':'reference','code produit':'reference',
    // Catégorie
    'categorie':'category','category':'category','famille':'category','type':'category','gamme':'category',
    // Prix
    'prix':'base_price','prix ht':'base_price','prix ht e':'base_price','tarif ht':'base_price',
    'tarif':'base_price','base price':'base_price','pu ht':'base_price','prix unitaire':'base_price',
    'prix unitaire ht':'base_price','prix achat':'base_price','base_price':'base_price',
    // Description
    'description':'description','descriptif':'description','details':'description',
    // Description courte
    'description courte':'short_desc','resume':'short_desc','short desc':'short_desc',
    'accroche':'short_desc','sous titre':'short_desc','short_desc':'short_desc',
    // Marque
    'marque':'brand','brand':'brand','fabricant':'brand',
    // Matière
    'matiere':'material','material':'material','composition':'material','tissu':'material',
    // Poids
    'poids':'weight','weight':'weight','poids g':'weight','grammage':'weight','grammes':'weight',
    // Genre
    'genre':'gender','gender':'gender','sexe':'gender','target':'gender','cible':'gender',
    // Code packshot
    'packshot_code':'packshot_code','packshot':'packshot_code','code_packshot':'packshot_code','code packshot':'packshot_code',
    // Image
    'image':'main_image','photo':'main_image','url image':'main_image','lien image':'main_image',
    'visuel':'main_image','main image':'main_image','main_image':'main_image',
    // Publié
    'publie':'is_published','is published':'is_published','en ligne':'is_published',
    'actif':'is_published','visible':'is_published','is_published':'is_published',
    // Vedette
    'vedette':'is_featured','is featured':'is_featured','mis en avant':'is_featured','is_featured':'is_featured',
    // Couleurs (noms)
    'couleurs':'colors','colors':'colors','coloris':'colors','couleur':'colors','color':'colors',
    // Couleurs (codes hex)
    'colors hexa':'colors_hex','colors hex':'colors_hex','couleurs hexa':'colors_hex',
    'couleurs hex':'colors_hex','hexa':'colors_hex','hex':'colors_hex',
    'code couleur':'colors_hex','code hex':'colors_hex','color hexa':'colors_hex',
    'color hex':'colors_hex','couleur hexa':'colors_hex','couleur hex':'colors_hex',
    'colors_hexa':'colors_hex','colors_hex':'colors_hex',
    // Tailles
    'tailles':'sizes','sizes':'sizes','pointures':'sizes','taille':'sizes','size':'sizes',
    // Ordre
    'ordre':'sort_order','sort order':'sort_order','position':'sort_order','sort_order':'sort_order',
  };

  // Parser CSV complet — respecte les guillemets (valeurs avec virgules/points-virgules)
  const parseCSVLine = (line, sep) => {
    const result = [];
    let cur = '', inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQ && line[i + 1] === '"') { cur += '"'; i++; }
        else inQ = !inQ;
      } else if (ch === sep && !inQ) {
        result.push(cur.trim()); cur = '';
      } else { cur += ch; }
    }
    result.push(cur.trim());
    return result;
  };

  const handleImportFile = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result.replace(/﻿/g,'').trim();
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) { toast.error('Fichier vide ou invalide'); return; }
      const sep = lines[0].includes(';') ? ';' : lines[0].includes('\t') ? '\t' : ',';
      const headers = parseCSVLine(lines[0], sep).map(h => PROD_HEADER_MAP[normHeader(h)] ?? normHeader(h));
      const stripQuotes = (v) => v.replace(/^["']+|["']+$/g, '').trim();
      const rawRows = lines.slice(1).map(line => {
        const vals = parseCSVLine(line, sep).map(stripQuotes);
        const obj = {};
        headers.forEach((h, i) => { if (vals[i] !== undefined) obj[h] = vals[i]; });
        return obj;
      }).filter(r => r.name || r.reference);

      if (!rawRows.length) { toast.error('Aucun produit valide détecté'); return; }

      // ── Regroupement par référence (ou par nom si pas de référence) ──
      // Parse un hex ou plusieurs séparés par / (ex: "000000 / e0005a" → ["#000000","#E0005A"])
      const parseHexList = (h) => {
        if (!h) return ['#000000'];
        return h.split('/').map(part => {
          const clean = part.trim().replace(/^#/, '');
          return /^[0-9a-fA-F]{3,6}$/.test(clean) ? '#' + clean.toUpperCase() : null;
        }).filter(Boolean);
      };

      const grouped = new Map();
      rawRows.forEach(row => {
        const key = (row.reference || row.name || '').trim().toLowerCase();
        if (!grouped.has(key)) {
          grouped.set(key, { ...row, _colorPairs: [], _sizes: [] });
        }
        const g = grouped.get(key);

        // Fusionner couleurs — associe chaque nom à ses hexes (support / pour couleurs bicolores)
        const rowColors = (row.colors    || '').split(';').map(c => c.trim()).filter(Boolean);
        const rowHexes  = (row.colors_hex|| '').split(';').map(h => h.trim()).filter(Boolean);
        rowColors.forEach((colorName, idx) => {
          const hexes = parseHexList(rowHexes[idx] || '');
          if (!g._colorPairs.some(p => p.name.toLowerCase() === colorName.toLowerCase())) {
            g._colorPairs.push({ name: colorName, hex: hexes[0], hexes });
          }
        });

        // Fusionner tailles
        const rowSizes = (row.sizes || '').split(';').map(s => s.trim()).filter(Boolean);
        rowSizes.forEach(s => { if (!g._sizes.includes(s)) g._sizes.push(s); });

        if (!g.name && row.name) g.name = row.name;
      });

      // Reconstruire — colors = JSON sérialisé pour conserver les hex
      const rows = Array.from(grouped.values()).map(g => ({
        ...g,
        colors:      JSON.stringify(g._colorPairs),
        sizes:       g._sizes.join(';'),
        colors_hex:  undefined,
        _colorPairs: undefined,
        _sizes:      undefined,
      })).filter(r => r.name);

      if (!rows.length) { toast.error('Aucun produit valide détecté (colonne "Nom" requise)'); return; }

      const merged = rawRows.length - rows.length;
      if (merged > 0) toast.info(`${merged} variante(s) fusionnée(s) → ${rows.length} produit(s) unique(s)`);

      setImportData(rows); setImportErrors([]); setImportModal(true);
    };
    reader.readAsText(file, 'UTF-8');
    e.target.value = '';
  };

  const runImport = async () => {
    setImporting(true);
    try {
      const { data } = await productAPI.bulkImport(importData);
      toast.success(data.message || `${data.data?.imported || 0} produit(s) importé(s) ✓`);
      const errs = data.data?.errors || [];
      setImportErrors(errs);
      if (!errs.length) { setImportModal(false); load(); }
      else load();
    } catch (err) { toast.error(err.response?.data?.message || 'Erreur import'); }
    finally { setImporting(false); }
  };

  const handleExportProducts = async () => {
    try {
      const params = { per_page: 10000, ...SORT_MAP[sortBy] };
      if (search)            params.search    = search;
      if (filterPub !== '')  params.published = parseInt(filterPub);
      if (filterCat)         params.category  = filterCat;
      if (filterFeat !== '') params.featured  = parseInt(filterFeat);
      if (filterGender)      params.gender    = filterGender;
      const { data } = await productAPI.list(params);
      const products = data.data || [];
      if (!products.length) { toast.info('Aucun produit à exporter'); return; }
      const headers = ['Nom','Référence','Catégorie','Genre','Code packshot','Prix HT','Description','Description courte','Marque','Matière','Poids (g)','Image','Publié','Couleurs','Colors_Hexa','Tailles','Ordre'];
      const rows = [
        headers.join(';'),
        ...products.map(p => {
          const colors = Array.isArray(p.available_colors)
            ? p.available_colors
            : (() => { try { return JSON.parse(p.available_colors || '[]'); } catch { return []; } })();
          const sizes = Array.isArray(p.available_sizes)
            ? p.available_sizes
            : (() => { try { return JSON.parse(p.available_sizes || '[]'); } catch { return []; } })();
          const cols = [
            p.name ?? '',
            p.reference ?? '',
            p.category_name ?? '',
            p.gender ?? '',
            p.packshot_code ?? '',
            p.base_price ?? '',
            p.description ?? '',
            p.short_desc ?? '',
            p.brand ?? '',
            p.material ?? '',
            p.weight ?? '',
            p.main_image ?? '',
            p.is_published ? 'oui' : 'non',
            colors.map(c => c.name || c).join(';'),
            colors.map(c => (c.hexes?.length > 1 ? c.hexes : [c.hex || '#000000']).join('/')).join(';'),
            sizes.join(';'),
            p.sort_order ?? 0,
          ];
          return cols.map(v => `"${String(v).replace(/"/g,'""')}"`).join(';');
        })
      ];
      const blob = new Blob(['﻿' + rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = `catalogue_produits_${new Date().toISOString().split('T')[0]}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${products.length} produit(s) exporté(s) ✓`);
    } catch { toast.error('Erreur lors de l\'export'); }
  };

  const toggleSelect  = (id) => setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const isAllSelected = (list) => list.length > 0 && list.every(i => selectedIds.has(i.id));
  const toggleAll     = (list) => setSelectedIds(isAllSelected(list) ? new Set() : new Set(list.map(i => i.id)));
  const bulkDelete    = async () => {
    let ok = 0;
    for (const id of selectedIds) { try { await productAPI.delete(id); ok++; } catch {} }
    toast.success(`${ok} produit(s) supprimé(s)`);
    setSelectedIds(new Set()); setBulkConfirm(false); load();
  };

  const handleApplyPackshots = async () => {
    setApplyingPS(true);
    try {
      const { data } = await productAPI.applyPackshots();
      toast.success(data.message || 'Packshots associés ✓');
      load(); // rafraîchit la liste pour mettre à jour les badges mockup_count
    } catch (err) { toast.error(err.response?.data?.message || 'Erreur'); }
    finally { setApplyingPS(false); }
  };

  const openNew  = () => { setEditing(null); setForm(emptyForm); setActiveTab('info'); setEditModal(true); };
  const openEdit = async (p) => {
    setEditing(p);
    setActiveTab('info');
    setEditModal(true);
    // Charger le détail complet depuis la DB (images fraîches)
    try {
      const { data } = await productAPI.get(p.id);
      const detail = data.data;
      setForm({ ...emptyForm, ...detail,
        available_colors: normalizeColors(detail.available_colors),
        technique_ids:    (detail.techniques || []).map(t => String(t.id)),
        product_images:   detail.images || [],
      });
    } catch {
      setForm({ ...emptyForm, ...p,
        available_colors: normalizeColors(p.available_colors),
        technique_ids:    (p.techniques || []).map(t => String(t.id)),
        product_images:   p.images || [],
      });
    }
  };

  const save = async () => {
    if (!form.name || !form.reference || !form.category_id) { toast.error('Nom, référence et catégorie requis'); return; }
    setSaving(true);
    try {
      let productId;
      if (editing) {
        await productAPI.update(editing.id, form);
        productId = editing.id;
      } else {
        const res = await productAPI.create(form);
        productId = res.data.data?.id;
      }

      // Synchroniser les images galerie pour un nouveau produit
      if (!editing && productId && (form.product_images || []).length > 0) {
        for (const img of form.product_images) {
          try { await productAPI.addImage(productId, { url: img.url, alt_text: img.alt_text || '' }); }
          catch { /* non bloquant */ }
        }
      }

      toast.success(editing ? 'Produit mis à jour ✓' : 'Produit créé ✓');
      setEditModal(false); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Erreur'); }
    finally { setSaving(false); }
  };

  const togglePublish = async (p) => {
    try { await productAPI.togglePublish(p.id); toast.success(p.is_published ? 'Dépublié' : 'Publié ✓'); load(); }
    catch { toast.error('Erreur'); }
  };

  const del = async (id) => {
    try { await productAPI.delete(id); toast.success('Produit supprimé'); load(); }
    catch { toast.error('Erreur suppression'); }
    setConfirm(null);
  };

  const addColor = () => {
    if (!newColor.name) return;
    const hexes = newColor.hexes.filter(h => h);
    f('available_colors', [...(form.available_colors || []), {
      name: newColor.name, hex: hexes[0] || '#000000', hexes: hexes.length > 0 ? hexes : ['#000000'],
    }]);
    setNewColor({ name: '', hexes: ['#000000'] });
  };

  const removeColor = (name) => f('available_colors', (form.available_colors || []).filter(c => c.name !== name));

  const sizes = availableSizes;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <div className="admin-page-header">
        <div><h1 className="admin-page-title">Produits</h1><p className="admin-page-subtitle">{meta?.total || 0} produits</p></div>
        <div style={{ display:'flex', gap:8 }}>
          <Button variant="secondary" onClick={handleExportProducts}>⬇ Export CSV</Button>
          <label className="btn btn-secondary btn-md" style={{ cursor:'pointer' }}>
            ⬆ Import CSV
            <input ref={importRef} type="file" accept=".csv,.txt" onChange={handleImportFile} hidden />
          </label>
          <Button variant="secondary" loading={applyingPS} onClick={handleApplyPackshots} title="Associer automatiquement les packshots de la médiathèque aux produits selon leur code packshot">
            🎨 Packshots
          </Button>
          <Button variant="primary" onClick={openNew}>+ Nouveau produit</Button>
        </div>
      </div>

      <div className="admin-page-content">

        {/* ── Aide import CSV ── */}
        <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-md)', padding:'12px 16px', marginBottom:'var(--space-md)', fontSize:12, color:'var(--text-muted)', display:'flex', alignItems:'flex-start', gap:10 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink:0, marginTop:1, color:'var(--info)' }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          <div>
            <span style={{ fontWeight:700, color:'var(--text-secondary)' }}>Format CSV d'import —</span>
            {' '}Séparateur <code style={{ background:'var(--bg-page)', padding:'1px 5px', borderRadius:3 }}>;</code> ou <code style={{ background:'var(--bg-page)', padding:'1px 5px', borderRadius:3 }}>,</code>
            {' '}·{' '}
            <span style={{ color:'var(--brand-accent)', fontWeight:600 }}>Nom</span> (requis)
            {' · '}Référence · Catégorie · Genre (<code style={{ background:'var(--bg-page)', padding:'1px 4px', borderRadius:3 }}>homme</code> / <code style={{ background:'var(--bg-page)', padding:'1px 4px', borderRadius:3 }}>femme</code> / <code style={{ background:'var(--bg-page)', padding:'1px 4px', borderRadius:3 }}>enfant</code> / <code style={{ background:'var(--bg-page)', padding:'1px 4px', borderRadius:3 }}>unisexe</code>)
            {' · '}Code packshot · Prix HT · Description · Description courte · Marque · Matière · Poids (g) · Image (URL)
            {' · '}Publié (<code style={{ background:'var(--bg-page)', padding:'1px 4px', borderRadius:3 }}>oui</code> / <code style={{ background:'var(--bg-page)', padding:'1px 4px', borderRadius:3 }}>non</code>)
            {' · '}Couleurs (séparées par <code style={{ background:'var(--bg-page)', padding:'1px 4px', borderRadius:3 }}>;</code>)
            {' · '}Colors_Hexa — multi-teintes séparées par <code style={{ background:'var(--bg-page)', padding:'1px 4px', borderRadius:3 }}>/</code> (ex : <code style={{ background:'var(--bg-page)', padding:'1px 4px', borderRadius:3 }}>000000/e0005a</code>)
            {' · '}Tailles (séparées par <code style={{ background:'var(--bg-page)', padding:'1px 4px', borderRadius:3 }}>;</code>)
          </div>
        </div>

        <div className="admin-card">
          <div style={{ display:'flex', gap:8, flexWrap:'wrap', padding:'var(--space-md) var(--space-lg)', borderBottom:'1px solid var(--border)', alignItems:'center' }}>
            {/* Recherche */}
            <div className="search-bar" style={{ minWidth:200, flex:1, maxWidth:280 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input className="form-input" placeholder="Nom, référence…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} style={{ paddingLeft:36 }} />
            </div>

            {/* Statut publication */}
            <select className="filter-select" value={filterPub} onChange={e => { setFilterPub(e.target.value); setPage(1); }}>
              <option value="">Tous les statuts</option>
              <option value="1">🌐 En ligne</option>
              <option value="0">📝 Brouillon</option>
            </select>

            {/* Catégorie */}
            <select className="filter-select" value={filterCat} onChange={e => { setFilterCat(e.target.value); setPage(1); }}>
              <option value="">Toutes les catégories</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>

            {/* Genre */}
            <select className="filter-select" value={filterGender} onChange={e => { setFilterGender(e.target.value); setPage(1); }}>
              <option value="">Tous les genres</option>
              <option value="homme">👔 Homme</option>
              <option value="femme">👗 Femme</option>
              <option value="enfant">🧒 Enfant</option>
              <option value="unisexe">👕 Unisexe</option>
            </select>

            {/* Vedette */}
            <select className="filter-select" value={filterFeat} onChange={e => { setFilterFeat(e.target.value); setPage(1); }}>
              <option value="">Vedette : tous</option>
              <option value="1">⭐ À la une</option>
              <option value="0">Normal</option>
            </select>

            {/* Tri */}
            <select className="filter-select" value={sortBy} onChange={e => { setSortBy(e.target.value); setPage(1); }}>
              <option value="newest">⬇ Plus récent</option>
              <option value="oldest">⬆ Plus ancien</option>
              <option value="name_az">A → Z</option>
              <option value="name_za">Z → A</option>
              <option value="price_asc">Prix croissant</option>
              <option value="price_desc">Prix décroissant</option>
            </select>

            {/* Reset */}
            {hasFilters && (
              <button onClick={resetFilters} style={{ fontSize:12, color:'var(--text-muted)', background:'none', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', padding:'6px 10px', cursor:'pointer', whiteSpace:'nowrap' }}>
                ✕ Réinitialiser
              </button>
            )}
          </div>

          {loading ? <div className="page-loader"><div className="spinner" /></div> :
            !products.length ? <EmptyState icon="👕" title="Aucun produit" action={<Button variant="primary" onClick={openNew}>Créer un produit</Button>} /> :
            <>{selectedIds.size > 0 && (
              <div style={{display:'flex',alignItems:'center',gap:12,padding:'8px 16px',background:'rgba(230,57,70,.06)',borderRadius:'var(--radius-sm)',border:'1px solid rgba(230,57,70,.2)',marginBottom:'var(--space-sm)'}}>
                <span style={{fontSize:13,fontWeight:600,color:'var(--brand-accent)'}}>{selectedIds.size} sélectionné(s)</span>
                <button style={{marginLeft:'auto',fontSize:12,color:'var(--text-muted)',background:'none',border:'none',cursor:'pointer'}} onClick={()=>setSelectedIds(new Set())}>Tout désélectionner</button>
                <Button size="sm" variant="ghost" onClick={()=>setBulkConfirm(true)} style={{color:'var(--error)'}}>🗑 Supprimer ({selectedIds.size})</Button>
              </div>
            )}
            <table className="data-table">
              <thead><tr><th style={{width:36}}><input type="checkbox" checked={isAllSelected(products)} onChange={()=>toggleAll(products)} /></th><th>Image</th><th>Produit</th><th>Catégorie</th><th>Réf.</th><th>Prix</th><th>Couleurs</th><th>Maquette</th><th>Publié</th><th>Actions</th></tr></thead>
              <tbody>
                {products.map(p => (
                  <tr key={p.id}>
                    <td><input type="checkbox" checked={selectedIds.has(p.id)} onChange={()=>toggleSelect(p.id)} /></td>
                    <td>
                      <div onClick={() => setPreviewProd(p)} style={{ cursor: 'pointer', position: 'relative', display: 'inline-block' }} title="Aperçu des images">
                        {p.main_image
                          ? <img src={p.main_image} alt="" style={{ width: 48, height: 48, borderRadius: 'var(--radius-sm)', objectFit: 'cover', border: '1px solid var(--border)', display: 'block' }} />
                          : <div style={{ width: 48, height: 48, borderRadius: 'var(--radius-sm)', background: 'var(--bg-page)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, border: '1px solid var(--border)' }}>👕</div>
                        }
                        {(p.images?.length > 1) && <span style={{ position: 'absolute', bottom: 2, right: 2, background: 'rgba(0,0,0,.6)', color: '#fff', fontSize: 9, fontWeight: 700, padding: '1px 4px', borderRadius: 3 }}>+{p.images.length - 1}</span>}
                      </div>
                    </td>
                    <td>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{p.name}</div>
                      {p.is_featured && <span style={{ fontSize: 10, color: 'var(--brand-gold)', fontWeight: 700 }}>⭐ À la une</span>}
                    </td>
                    <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{p.category_name}</td>
                    <td><code style={{ fontSize: 11, background: 'var(--bg-page)', padding: '2px 6px', borderRadius: 4 }}>{p.reference}</code></td>
                    <td style={{ fontWeight: 700, color: 'var(--brand-accent)' }}>{parseFloat(parseFloat(p.base_price) || 0).toFixed(2)} €</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {normalizeColors(p.available_colors).slice(0, 5).map((c, ci) => (
                          <div key={c.name + ci} style={{ width: 18, height: 18, borderRadius: '50%', ...splitCircle(c.hexes), border: '2px solid white', boxShadow: '0 0 0 1px var(--border)', flexShrink: 0 }} title={`${c.name} (${(c.hexes||[c.hex]).join(' / ')})`} />
                        ))}
                        {normalizeColors(p.available_colors).length > 5 && <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>+{normalizeColors(p.available_colors).length - 5}</span>}
                      </div>
                    </td>
                    <td style={{textAlign:'center'}}>
                      {parseInt(p.mockup_count) > 0
                        ? <span title={`${p.mockup_count} maquette(s) associée(s)`} style={{display:'inline-flex',alignItems:'center',gap:4,padding:'2px 8px',borderRadius:'var(--radius-full)',fontSize:11,fontWeight:700,background:'#dcfce7',color:'#065f46',outline:'1px solid #a7f3d0'}}>
                            🎨 {p.mockup_count}
                          </span>
                        : <span title="Aucune maquette associée" style={{display:'inline-flex',alignItems:'center',gap:4,padding:'2px 8px',borderRadius:'var(--radius-full)',fontSize:11,fontWeight:700,background:'#fef2f2',color:'#991b1b',outline:'1px solid #fecaca'}}>
                            ✕ Aucune
                          </span>
                      }
                    </td>
                    <td>
                      <button
                        onClick={() => togglePublish(p)}
                        title={p.is_published ? "Cliquer pour dépublier (reste visible dans l'admin)" : "Cliquer pour publier sur le site"}
                        style={{ padding: '3px 10px', borderRadius: 'var(--radius-full)', fontSize: 11, fontWeight: 700, border: 'none', cursor: 'pointer',
                          background: p.is_published ? '#dcfce7' : '#f1f5f9',
                          color: p.is_published ? '#065f46' : '#64748b',
                          outline: `1px solid ${p.is_published ? '#a7f3d0' : '#cbd5e1'}` }}>
                        {p.is_published ? '🌐 En ligne' : '📝 Brouillon'}
                      </button>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <Button size="sm" variant="secondary" onClick={() => openEdit(p)}>✏️ Modifier</Button>
                        <Button size="sm" variant="ghost" onClick={() => setConfirm(p.id)} title="Supprimer">🗑</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></>
          }
          <div style={{ padding: 'var(--space-md) var(--space-lg)', borderTop: '1px solid var(--border)' }}>
            <Pagination meta={meta} onPageChange={setPage} />
          </div>
        </div>
      </div>

      {/* ── Modal édition produit ── */}
      <Modal isOpen={editModal} onClose={() => setEditModal(false)} title={editing ? `Modifier : ${editing.name}` : 'Nouveau produit'} size="xl"
        footer={<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}><Button variant="ghost" onClick={() => setEditModal(false)}>Annuler</Button><Button variant="primary" loading={saving} onClick={save}>Enregistrer</Button></div>}
      >
        {/* Onglets */}
        <div style={{ display: 'flex', gap: 2, marginBottom: 20, borderBottom: '1px solid var(--border)', paddingBottom: 0 }}>
          {[['info','ℹ️ Informations'],['images','🖼 Images'],['options','🎨 Options'],['seo','🔍 SEO']].map(([k, l]) => (
            <button key={k} onClick={() => setActiveTab(k)} style={{ padding: '8px 16px', fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer', borderBottom: activeTab === k ? '2px solid var(--brand-accent)' : '2px solid transparent', color: activeTab === k ? 'var(--brand-accent)' : 'var(--text-secondary)', background: 'none', marginBottom: -1 }}>
              {l}
            </button>
          ))}
        </div>

        {/* ── Tab: Informations ── */}
        {activeTab === 'info' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)' }}>
            <FormField label="Nom du produit" required><Input value={form.name} onChange={e => f('name', e.target.value)} placeholder="T-Shirt Corporate Pro" /></FormField>
            <FormField label="Référence" required><Input value={form.reference} onChange={e => f('reference', e.target.value)} placeholder="TSH-001" /></FormField>
            <FormField label="Catégorie" required hint={catError ? `❌ Erreur : ${catError}` : categories.length === 0 ? '⏳ Chargement…' : `✓ ${categories.length} catégorie(s)`}>
              <Select value={form.category_id} onChange={e => f('category_id', e.target.value)}>
                <option value="">{categories.length === 0 ? '⚠ Aucune catégorie disponible' : 'Choisir une catégorie…'}</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </FormField>
            <FormField label="Prix de base (€ HT)"><Input type="number" step="0.01" value={form.base_price} onChange={e => f('base_price', e.target.value)} placeholder="12.90" /></FormField>
            <FormField label="Marque"><Input value={form.brand || ''} onChange={e => f('brand', e.target.value)} /></FormField>
            <FormField label="Matière"><Input value={form.material || ''} onChange={e => f('material', e.target.value)} placeholder="100% coton" /></FormField>
            <FormField label="Poids (g)"><Input type="number" value={form.weight || ''} onChange={e => f('weight', e.target.value)} placeholder="180" /></FormField>
            <FormField label="Genre">
              <select className="form-input form-select" value={form.gender || ''} onChange={e => f('gender', e.target.value)}>
                <option value="">— Non défini —</option>
                <option value="homme">👔 Homme</option>
                <option value="femme">👗 Femme</option>
                <option value="enfant">🧒 Enfant</option>
                <option value="unisexe">👕 Unisexe</option>
              </select>
            </FormField>
            <FormField label="Code packshot" hint="Ex: tshirt, softshell, polaire — les fichiers [code]_front_[couleur].jpg seront auto-associés">
              <Input value={form.packshot_code || ''} onChange={e => f('packshot_code', e.target.value)} placeholder="tshirt" />
            </FormField>
            <FormField label="Nombre de faces">
              <Select value={form.faces_count} onChange={e => f('faces_count', parseInt(e.target.value))}>
                <option value={2}>2 faces (Avant / Arrière)</option>
                <option value={3}>3 faces (Avant / Arrière / Manche)</option>
              </Select>
            </FormField>
            <div style={{ gridColumn: '1/-1' }}>
              <FormField label="Description courte">
                <Textarea rows={2} value={form.short_desc || ''} onChange={e => f('short_desc', e.target.value)} placeholder="Brève description pour les listes produits" />
              </FormField>
            </div>
            <div style={{ gridColumn: '1/-1' }}>
              <FormField label="Description complète">
                <Textarea rows={4} value={form.description || ''} onChange={e => f('description', e.target.value)} />
              </FormField>
            </div>
            <div style={{ display: 'flex', gap: 24 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!form.is_published} onChange={e => f('is_published', e.target.checked)} /> Publié en ligne
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!form.is_featured} onChange={e => f('is_featured', e.target.checked)} /> ⭐ Produit à la une
              </label>
            </div>
          </div>
        )}

        {/* ── Tab: Images ── */}
        {activeTab === 'images' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>

            {/* Galerie produit */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>🖼 Galerie du produit</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Cliquez ⭐ pour définir l'image principale</div>
              </div>

              {/* Images actuelles */}
              {(form.product_images || []).length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 10, marginBottom: 16 }}>
                  {(form.product_images || []).map((img, i) => {
                    const isMain = form.main_image === img.url;
                    return (
                      <div key={img.id || i} style={{ position: 'relative', borderRadius: 'var(--radius-md)', overflow: 'hidden', border: isMain ? '3px solid var(--brand-accent)' : '2px solid var(--border)', background: 'var(--bg-page)' }}>
                        <img src={img.url} alt={img.alt_text || ''} style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block' }} />
                        {isMain && (
                          <div style={{ position: 'absolute', top: 4, left: 4, background: 'var(--brand-accent)', color: 'white', fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4 }}>
                            ⭐ Principal
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: 4, padding: '6px', background: 'rgba(0,0,0,.5)', position: 'absolute', bottom: 0, left: 0, right: 0 }}>
                          {!isMain && (
                            <button
                              onClick={() => f('main_image', img.url)}
                              title="Définir comme image principale"
                              style={{ flex: 1, background: 'rgba(255,255,255,.2)', border: 'none', color: 'white', borderRadius: 4, cursor: 'pointer', fontSize: 12, padding: '3px 0' }}>
                              ⭐
                            </button>
                          )}
                          <button
                            onClick={async () => {
                              if (editing?.id && img.id) {
                                try {
                                  await productAPI.deleteImage(editing.id, img.id);
                                  const newImgs = (form.product_images || []).filter(x => x.id !== img.id);
                                  f('product_images', newImgs);
                                  if (isMain) f('main_image', newImgs[0]?.url || '');
                                  toast.success('Image supprimée');
                                } catch { toast.error('Erreur'); }
                              } else {
                                const newImgs = (form.product_images || []).filter((_, idx) => idx !== i);
                                f('product_images', newImgs);
                                if (isMain) f('main_image', newImgs[0]?.url || '');
                              }
                            }}
                            title="Supprimer cette image"
                            style={{ flex: 1, background: 'rgba(220,50,50,.7)', border: 'none', color: 'white', borderRadius: 4, cursor: 'pointer', fontSize: 12, padding: '3px 0' }}>
                            🗑
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ padding: 20, border: '2px dashed var(--border)', borderRadius: 'var(--radius-md)', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, marginBottom: 16 }}>
                  Aucune image — ajoutez-en depuis la médiathèque ci-dessous
                </div>
              )}

              {/* Upload direct */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
                <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
                  {uploading ? '⏳ Upload…' : '📤 Uploader une image'}
                  <input ref={fileRef} type="file" accept="image/*" multiple onChange={async (e) => {
                    const files = Array.from(e.target.files);
                    if (!files.length) return;
                    setUploading(true);
                    for (const file of files) {
                      const fd = new FormData();
                      fd.append('file', file);
                      fd.append('folder', 'products');
                      try {
                        const { data } = await mediaAPI.upload(fd);
                        const url = data.data?.url;
                        if (url) {
                          const newImg = { url, alt_text: file.name.replace(/\.[^/.]+$/, '') };
                          if (editing?.id) {
                            const res = await productAPI.addImage(editing.id, newImg);
                            newImg.id = res.data.data?.id;
                          }
                          const imgs = [...(form.product_images || []), newImg];
                          f('product_images', imgs);
                          if (!form.main_image) f('main_image', url);
                        }
                      } catch { toast.error('Échec upload'); }
                    }
                    setUploading(false);
                    fileRef.current.value = '';
                  }} hidden />
                </label>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>ou choisissez depuis la médiathèque</span>
              </div>

              {/* Sélectionner depuis la médiathèque */}
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8, color: 'var(--text-secondary)' }}>📁 Médiathèque (dossier products)</div>
              {!medias.length ? (
                <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Aucune image. Uploadez via le bouton ci-dessus.</p>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: 8, maxHeight: 220, overflowY: 'auto', padding: 4 }}>
                  {medias.map(m => {
                    const alreadyAdded = (form.product_images || []).some(x => x.url === m.url);
                    return (
                      <div
                        key={m.id}
                        onClick={() => {
                          if (alreadyAdded) return;
                          const newImg = { url: m.url, alt_text: m.original_name || '', id: null };
                          if (editing?.id) {
                            productAPI.addImage(editing.id, newImg).then(r => {
                              newImg.id = r.data.data?.id;
                            }).catch(() => {});
                          }
                          const imgs = [...(form.product_images || []), newImg];
                          f('product_images', imgs);
                          if (!form.main_image) f('main_image', m.url);
                        }}
                        style={{ position: 'relative', aspectRatio: '1', cursor: alreadyAdded ? 'default' : 'pointer', borderRadius: 'var(--radius-sm)', overflow: 'hidden', border: alreadyAdded ? '2px solid var(--brand-accent)' : '2px solid var(--border)', opacity: alreadyAdded ? .6 : 1, transition: 'all .15s' }}
                        title={alreadyAdded ? 'Déjà ajoutée' : `Ajouter : ${m.original_name}`}
                      >
                        <img src={m.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        {alreadyAdded && (
                          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(230,57,70,.2)' }}>
                            <span style={{ color: 'var(--brand-accent)', fontSize: 18 }}>✓</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Image principale URL manuelle */}
            <div style={{ padding: 12, background: 'var(--bg-page)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)' }}>
              <FormField label="🌟 URL image principale" hint="Définie automatiquement via ⭐ — ou saisissez manuellement">
                <Input value={form.main_image || ''} onChange={e => f('main_image', e.target.value)} placeholder="https://…" />
              </FormField>
            </div>
          </div>
        )}

        {/* ── Tab: Options ── */}
        {activeTab === 'options' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>
            {/* Couleurs */}
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>🎨 Couleurs disponibles</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                {normalizeColors(form.available_colors).map(c => (
                  <div key={c.name} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', background: 'var(--bg-page)', border: '1px solid var(--border)', borderRadius: 'var(--radius-full)', fontSize: 12 }}>
                    <div style={{ width: 16, height: 16, borderRadius: '50%', ...splitCircle(c.hexes), border: '1px solid rgba(0,0,0,.15)', flexShrink: 0 }} title={(c.hexes||[c.hex]).join(' / ')} />
                    {c.name}
                    {c.hexes?.length > 1 && <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>({c.hexes.length})</span>}
                    <button onClick={() => removeColor(c.name)} style={{ color: 'var(--text-muted)', fontSize: 14, cursor: 'pointer', lineHeight: 1 }}>×</button>
                  </div>
                ))}
                {!(form.available_colors || []).length && <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Aucune couleur ajoutée</p>}
              </div>
              {/* Ajout d'une nouvelle couleur (supporte plusieurs hex séparés par /) */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                  {(newColor.hexes || ['#000000']).map((h, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <input type="color" value={h} onChange={e => setNewColor(p => { const hs=[...(p.hexes||['#000000'])]; hs[i]=e.target.value; return {...p,hexes:hs}; })} style={{ width: 36, height: 36, cursor: 'pointer', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: 2 }} />
                      {i > 0 && <button onClick={() => setNewColor(p => ({...p, hexes: p.hexes.filter((_,j)=>j!==i)}))} style={{ color: 'var(--text-muted)', fontSize: 16, cursor: 'pointer', background: 'none', border: 'none', lineHeight: 1 }}>×</button>}
                    </div>
                  ))}
                  <button onClick={() => setNewColor(p => ({...p, hexes: [...(p.hexes||['#000000']), '#ffffff']}))} style={{ width: 36, height: 36, border: '1px dashed var(--border)', borderRadius: 'var(--radius-sm)', background: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text-muted)' }} title="Ajouter une teinte">+</button>
                  {(newColor.hexes||[]).length > 1 && (
                    <div style={{ width: 24, height: 24, borderRadius: '50%', ...splitCircle(newColor.hexes), border: '1px solid rgba(0,0,0,.1)', flexShrink:0 }} />
                  )}
                </div>
                <Input value={newColor.name} onChange={e => setNewColor(p => ({ ...p, name: e.target.value }))} placeholder="Nom (ex: Blanc/Navy, Rouge…)" style={{ flex: 1, minWidth: 150 }} onKeyDown={e => e.key === 'Enter' && addColor()} />
                <Button variant="secondary" onClick={addColor} disabled={!newColor.name}>+ Ajouter</Button>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                Cliquez sur <strong>+</strong> pour ajouter une 2ᵉ teinte (ex: manche blanche + corps noir → 2 couleurs dans le même cercle)
              </div>
            </div>

            {/* Tailles */}
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>📏 Tailles disponibles</div>
              {!sizesLoaded ? (
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Chargement…</div>
              ) : sizes.length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '8px 12px', background: 'var(--bg-page)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
                  Aucune taille configurée — ajoutez-en depuis <strong>Produits → Tailles</strong>.
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {sizes.map(s => (
                    <label key={s} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', border: `1.5px solid ${(form.available_sizes || []).includes(s) ? 'var(--brand-accent)' : 'var(--border)'}`, borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: (form.available_sizes || []).includes(s) ? 'rgba(230,57,70,.05)' : 'var(--bg-card)', color: (form.available_sizes || []).includes(s) ? 'var(--brand-accent)' : 'var(--text-secondary)', transition: 'all .15s' }}>
                      <input type="checkbox" checked={(form.available_sizes || []).includes(s)} hidden
                        onChange={e => { const cur = form.available_sizes || []; f('available_sizes', e.target.checked ? [...cur, s] : cur.filter(x => x !== s)); }}
                      />
                      {s}
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Techniques */}
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>⚙️ Techniques de marquage compatibles</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {techniques.map(t => (
                  <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', border: `1.5px solid ${(form.technique_ids || []).includes(String(t.id)) ? 'var(--brand-accent)' : 'var(--border)'}`, borderRadius: 'var(--radius-sm)', cursor: 'pointer', background: (form.technique_ids || []).includes(String(t.id)) ? 'rgba(230,57,70,.04)' : 'var(--bg-card)', transition: 'all .15s' }}>
                    <input type="checkbox" checked={(form.technique_ids || []).includes(String(t.id))}
                      onChange={e => { const ids = form.technique_ids || []; f('technique_ids', e.target.checked ? [...ids, String(t.id)] : ids.filter(x => x !== String(t.id))); }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{t.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>+{t.price_per_unit}€/pce · Setup {t.price_setup}€ · Min. {t.min_quantity} pcs</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Tab: SEO ── */}
        {activeTab === 'seo' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
            <div style={{ padding: 12, background: 'var(--info-bg)', borderRadius: 'var(--radius-sm)', fontSize: 13, color: 'var(--info)' }}>
              💡 Ces champs améliorent le référencement dans les moteurs de recherche. Si vides, le titre et la description du produit sont utilisés par défaut.
            </div>
            <FormField label="Titre SEO (meta title)" hint="Idéalement entre 50 et 60 caractères">
              <Input value={form.meta_title || ''} onChange={e => f('meta_title', e.target.value)} placeholder={form.name} maxLength={70} />
            </FormField>
            <FormField label="Description SEO (meta description)" hint="Idéalement entre 120 et 160 caractères">
              <Textarea rows={3} value={form.meta_description || ''} onChange={e => f('meta_description', e.target.value)} placeholder={form.short_desc} maxLength={200} />
            </FormField>
          </div>
        )}
      </Modal>

      <ConfirmDialog isOpen={!!confirm} onClose={() => setConfirm(null)} onConfirm={() => del(confirm)} title="Supprimer le produit" message="Cette action est irréversible. Le produit sera supprimé du catalogue." confirmLabel="Supprimer définitivement" danger />
      <ConfirmDialog isOpen={bulkConfirm} onClose={() => setBulkConfirm(false)} onConfirm={bulkDelete} title="Supprimer la sélection" message={`${selectedIds.size} produit(s) seront définitivement supprimés du catalogue.`} confirmLabel="Supprimer tout" danger />

      {/* ── Aperçu images produit ── */}
      {previewProd && (
        <div onClick={() => setPreviewProd(null)}
          style={{ position:'fixed', inset:0, zIndex:9999, background:'rgba(0,0,0,.8)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:12, padding:24, cursor:'pointer' }}>
          <div style={{ color:'#fff', fontWeight:700, fontSize:16, marginBottom:4 }}>{previewProd.name}</div>
          <div style={{ display:'flex', gap:10, flexWrap:'wrap', justifyContent:'center', maxWidth:900, maxHeight:'80vh', overflowY:'auto' }}
            onClick={e => e.stopPropagation()}>
            {[...(previewProd.main_image ? [{ url: previewProd.main_image }] : []), ...(previewProd.images || [])].filter((img, i, arr) => arr.findIndex(x => x.url === img.url) === i).map((img, i) => (
              <img key={i} src={img.url} alt="" style={{ maxHeight:260, maxWidth:280, objectFit:'contain', borderRadius:8, background:'#fff', padding:4, boxShadow:'0 4px 20px rgba(0,0,0,.4)' }} />
            ))}
          </div>
          <button onClick={() => setPreviewProd(null)} style={{ marginTop:8, color:'rgba(255,255,255,.7)', background:'none', border:'1px solid rgba(255,255,255,.3)', borderRadius:20, padding:'6px 16px', cursor:'pointer', fontSize:13 }}>Fermer</button>
        </div>
      )}

      {/* ── Modale import CSV produits ── */}
      <Modal isOpen={importModal} onClose={() => setImportModal(false)} title="Import produits CSV" size="lg"
        footer={
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end', alignItems:'center' }}>
            <span style={{ fontSize:13, color:'var(--text-muted)', flex:1 }}>{importData.length} produit(s) détecté(s)</span>
            <Button variant="ghost" onClick={() => setImportModal(false)}>Annuler</Button>
            <Button variant="primary" loading={importing} onClick={runImport}>Importer {importData.length} produit(s)</Button>
          </div>
        }
      >
        <div style={{ display:'flex', flexDirection:'column', gap:'var(--space-md)' }}>
          <div style={{ padding:12, background:'var(--info-bg)', borderRadius:'var(--radius-sm)', fontSize:13, color:'var(--info)' }}>
            📋 Colonnes acceptées&nbsp;: <strong>Nom</strong> (requis), Référence, Catégorie, Prix HT, Description, Description courte, Marque, Matière, Image, Publié (oui/non), Couleurs (séparées par <code>;</code>), Tailles (séparées par <code>;</code>)
          </div>
          <div style={{ maxHeight:320, overflowY:'auto', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)' }}>
            <table className="data-table" style={{ fontSize:12 }}>
              <thead><tr><th>Nom</th><th>Référence</th><th>Catégorie</th><th>Prix HT</th><th>Marque</th><th>Publié</th></tr></thead>
              <tbody>
                {importData.slice(0, 100).map((r, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight:600 }}>{r.name || <span style={{ color:'var(--error)' }}>⚠ manquant</span>}</td>
                    <td style={{ color:'var(--text-muted)' }}>{r.reference || '—'}</td>
                    <td>{r.category || '—'}</td>
                    <td>{r.base_price ? `${r.base_price} €` : '—'}</td>
                    <td>{r.brand || '—'}</td>
                    <td>{['1','oui','yes','true'].includes((r.is_published||'').toLowerCase()) ? '✓' : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {importData.length > 100 && <p style={{ fontSize:12, color:'var(--text-muted)' }}>… et {importData.length - 100} autres</p>}
          {importErrors.length > 0 && (
            <div style={{ padding:12, background:'var(--error-bg)', borderRadius:'var(--radius-sm)', fontSize:12, color:'var(--error)' }}>
              <strong>Erreurs ({importErrors.length}) :</strong>
              <ul style={{ marginTop:6, paddingLeft:16 }}>
                {importErrors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};


/* ════════════════════════════════════════════════════
   FACTURES AMÉLIORÉES — création directe + suppression
   ════════════════════════════════════════════════════ */
export const AdminInvoicesEnhancedPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [clients,  setClients]  = useState([]);
  const [meta,     setMeta]     = useState(null);
  const [page,     setPage]     = useState(1);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [status,   setStatus]   = useState('');
  const [modal,    setModal]    = useState(false);
  const [confirm,      setConfirm]     = useState(null);
  const [saving,       setSaving]      = useState(false);
  const [payModal,     setPayModal]    = useState(null);
  const [selIds,       setSelIds]      = useState(new Set());
  const [bulkConfirmI, setBulkConfirmI]= useState(false);
  const [payForm,  setPayForm]  = useState({ amount: '', method: 'bank_transfer', reference: '', paid_at: new Date().toISOString().split('T')[0], notes: '' });

  const vatRate = 20;
  const emptyLine = { description: '', quantity: 1, unit_price: 0, discount: 0 };
  const emptyForm = {
    client_id: '', client_snapshot: {}, items: [{ ...emptyLine }],
    discount_pct: 0, tax_rate: vatRate, notes: '',
    due_date: new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0],
    status: 'draft'
  };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(() => {
    setLoading(true);
    const p = { page, per_page: 16 };
    if (search) p.search = search;
    if (status) p.status = status;
    Promise.all([invoiceAPI.list(p), clientAPI.list({ per_page: 200 })])
      .then(([inv, cl]) => { setInvoices(inv.data.data || []); setMeta(inv.data.meta); setClients(cl.data.data || []); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [page, search, status]);

  useEffect(() => { load(); }, [load]);

  // Calculs dynamiques
  const computeTotals = (items, discPct, taxRate) => {
    const subtotal = items.reduce((s, i) => s + (parseFloat(i.quantity) || 0) * (parseFloat(i.unit_price) || 0) * (1 - (parseFloat(i.discount) || 0) / 100), 0);
    const discount = subtotal * (parseFloat(discPct) || 0) / 100;
    const taxable  = subtotal - discount;
    const tax      = taxable * (parseFloat(taxRate) || 0) / 100;
    return { subtotal: taxable, discount, tax, total: taxable + tax };
  };
  const totals = computeTotals(form.items, form.discount_pct, form.tax_rate);

  const addLine    = () => setForm(p => ({ ...p, items: [...p.items, { ...emptyLine }] }));
  const removeLine = (i) => setForm(p => ({ ...p, items: p.items.filter((_, idx) => idx !== i) }));
  const updateLine = (i, k, v) => setForm(p => ({ ...p, items: p.items.map((l, idx) => idx === i ? { ...l, [k]: v } : l) }));

  const selectClient = (clientId) => {
    const c = clients.find(x => x.id === parseInt(clientId));
    setForm(p => ({
      ...p,
      client_id: clientId,
      client_snapshot: c ? {
        company: c.company,
        first_name: c.first_name || c.contact_name?.split(' ')[0] || '',
        last_name: c.last_name || c.contact_name?.split(' ').slice(1).join(' ') || '',
        email: c.email, phone: c.phone, address: c.address,
        city: c.city, zip_code: c.zip_code, country: c.country
      } : {}
    }));
  };

  const save = async () => {
    if (!form.items.length) { toast.error('Ajoutez au moins une ligne'); return; }
    setSaving(true);
    try {
      await invoiceAPI.create({ ...form, ...computeTotals(form.items, form.discount_pct, form.tax_rate) });
      toast.success('Facture créée ✓');
      setModal(false);
      setForm(emptyForm);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Erreur'); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    try { await invoiceAPI.delete(id); toast.success('Facture supprimée'); load(); }
    catch { toast.error('Erreur suppression'); }
    setConfirm(null);
  };
  const toggleSelI  = (id) => setSelIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const isAllSelI   = (list) => list.length > 0 && list.every(i => selIds.has(i.id));
  const toggleAllI  = (list) => setSelIds(isAllSelI(list) ? new Set() : new Set(list.map(i => i.id)));
  const bulkDelI    = async () => {
    let ok = 0;
    for (const id of selIds) { try { await invoiceAPI.delete(id); ok++; } catch {} }
    toast.success(`${ok} facture(s) supprimée(s)`);
    setSelIds(new Set()); setBulkConfirmI(false); load();
  };

  const addPayment = async () => {
    if (!payForm.amount || parseFloat(payForm.amount) <= 0) { toast.error('Montant invalide'); return; }
    try {
      await invoiceAPI.addPayment(payModal, payForm);
      toast.success('Règlement enregistré ✓');
      setPayModal(null);
      setPayForm({ amount: '', method: 'bank_transfer', reference: '', paid_at: new Date().toISOString().split('T')[0], notes: '' });
      load();
    } catch { toast.error('Erreur'); }
  };

  const openPdf = (id) => { window.open(invoiceAPI.pdfUrl(id), '_blank'); };

  const statusLabels = { draft:'Brouillon', sent:'Envoyé', paid:'Payé', partial:'Partiel', overdue:'En retard', cancelled:'Annulé' };
  const statusBadge  = { draft:'badge-neutral', sent:'badge-info', paid:'badge-success', partial:'badge-warning', overdue:'badge-error', cancelled:'badge-neutral' };
  const methods      = { bank_transfer:'Virement', check:'Chèque', cash:'Espèces', card:'Carte', other:'Autre' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <div className="admin-page-header">
        <div><h1 className="admin-page-title">Factures</h1><p className="admin-page-subtitle">{meta?.total || 0} factures</p></div>
        <Button variant="primary" onClick={() => { setForm(emptyForm); setModal(true); }}>+ Nouvelle facture</Button>
      </div>

      <div className="admin-page-content">
        <div className="admin-card">
          <div className="admin-card-header">
            <div className="search-bar" style={{ maxWidth: 280 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input className="form-input" placeholder="Référence, client…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} style={{ paddingLeft: 36 }} />
            </div>
            <select className="filter-select" value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
              <option value="">Tous les statuts</option>
              {Object.entries(statusLabels).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>

          {loading ? <div className="page-loader"><div className="spinner" /></div> :
            !invoices.length ? <EmptyState icon="🧾" title="Aucune facture" action={<Button variant="primary" onClick={() => setModal(true)}>Créer une facture</Button>} /> :
            <>{selIds.size > 0 && (
              <div style={{display:'flex',alignItems:'center',gap:12,padding:'8px 16px',background:'rgba(230,57,70,.06)',borderRadius:'var(--radius-sm)',border:'1px solid rgba(230,57,70,.2)',marginBottom:'var(--space-sm)'}}>
                <span style={{fontSize:13,fontWeight:600,color:'var(--brand-accent)'}}>{selIds.size} sélectionné(s)</span>
                <button style={{marginLeft:'auto',fontSize:12,color:'var(--text-muted)',background:'none',border:'none',cursor:'pointer'}} onClick={()=>setSelIds(new Set())}>Tout désélectionner</button>
                <Button size="sm" variant="ghost" onClick={()=>setBulkConfirmI(true)} style={{color:'var(--error)'}}>🗑 Supprimer ({selIds.size})</Button>
              </div>
            )}
            <table className="data-table">
              <thead><tr><th style={{width:36}}><input type="checkbox" checked={isAllSelI(invoices)} onChange={()=>toggleAllI(invoices)} /></th><th>Référence</th><th>Client</th><th>Montant TTC</th><th>Restant dû</th><th>Échéance</th><th>Statut</th><th>Actions</th></tr></thead>
              <tbody>
                {invoices.map(inv => (
                  <tr key={inv.id}>
                    <td><input type="checkbox" checked={selIds.has(inv.id)} onChange={()=>toggleSelI(inv.id)} /></td>
                    <td><strong style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}>{inv.reference}</strong></td>
                    <td style={{ fontSize: 13 }}>{inv.client_company || (typeof inv.client_snapshot === 'object' ? inv.client_snapshot?.company : '—')}</td>
                    <td style={{ fontWeight: 700 }}>{parseFloat(inv.total_ttc || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</td>
                    <td style={{ color: parseFloat(inv.balance_due) > 0 ? 'var(--error)' : 'var(--success)', fontWeight: 600 }}>
                      {parseFloat(inv.balance_due || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{inv.due_date ? new Date(inv.due_date).toLocaleDateString('fr-FR') : '—'}</td>
                    <td><span className={`badge ${statusBadge[inv.status] || 'badge-neutral'}`}>{statusLabels[inv.status] || inv.status}</span></td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <Button size="sm" variant="secondary" onClick={() => openPdf(inv.id)} title="Aperçu PDF">📄</Button>
                        {inv.status !== 'paid' && inv.status !== 'cancelled' && (
                          <Button size="sm" variant="secondary" onClick={() => {
                            const due = parseFloat(inv.balance_due || 0);
                            const amt = due > 0 ? due : parseFloat(inv.total_ttc || 0);
                            setPayModal(inv.id);
                            setPayForm({ amount: amt.toFixed(2), method: 'bank_transfer', reference: '', paid_at: new Date().toISOString().split('T')[0], notes: '' });
                          }} title="Ajouter règlement">💳</Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => setConfirm(inv.id)} title="Supprimer">🗑</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></>
          }
          <div style={{ padding: 'var(--space-md) var(--space-lg)', borderTop: '1px solid var(--border)' }}>
            <Pagination meta={meta} onPageChange={setPage} />
          </div>
        </div>
      </div>

      {/* ── Modal création facture ── */}
      <Modal isOpen={modal} onClose={() => setModal(false)} title="Nouvelle facture" size="xl"
        footer={<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}><Button variant="ghost" onClick={() => setModal(false)}>Annuler</Button><Button variant="primary" loading={saving} onClick={save}>Créer la facture</Button></div>}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>
          {/* Client */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)' }}>
            <FormField label="Client" hint="Sélectionnez un client existant">
              <Select value={form.client_id} onChange={e => selectClient(e.target.value)}>
                <option value="">Client non enregistré</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.company || ''}{c.company && (c.first_name||c.last_name) ? ' — ' : ''}{[c.first_name,c.last_name].filter(Boolean).join(' ') || c.contact_name || c.email}</option>)}
              </Select>
            </FormField>
            <FormField label="Date d'échéance">
              <Input type="date" value={form.due_date} onChange={e => setForm(p => ({ ...p, due_date: e.target.value }))} />
            </FormField>
          </div>

          {/* Si pas de client sélectionné, champs manuels */}
          {!form.client_id && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)', padding: 16, background: 'var(--bg-page)', borderRadius: 'var(--radius-md)', border: '1px dashed var(--border)' }}>
              <div style={{ gridColumn: '1/-1', fontSize: 12, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: -4 }}>COORDONNÉES CLIENT (saisie manuelle)</div>
              <FormField label="Entreprise"><Input value={form.client_snapshot?.company || ''} onChange={e => setForm(p => ({ ...p, client_snapshot: { ...p.client_snapshot, company: e.target.value } }))} placeholder="Mon Entreprise SAS" /></FormField>
              <FormField label="Contact"><Input value={form.client_snapshot?.contact_name || ''} onChange={e => setForm(p => ({ ...p, client_snapshot: { ...p.client_snapshot, contact_name: e.target.value } }))} placeholder="Jean Dupont" /></FormField>
              <FormField label="Email"><Input type="email" value={form.client_snapshot?.email || ''} onChange={e => setForm(p => ({ ...p, client_snapshot: { ...p.client_snapshot, email: e.target.value } }))} placeholder="contact@entreprise.fr" /></FormField>
              <FormField label="Téléphone"><Input value={form.client_snapshot?.phone || ''} onChange={e => setForm(p => ({ ...p, client_snapshot: { ...p.client_snapshot, phone: e.target.value } }))} placeholder="+33 1 23 45 67 89" /></FormField>
              <div style={{ gridColumn: '1/-1' }}>
                <FormField label="Adresse"><Input value={form.client_snapshot?.address || ''} onChange={e => setForm(p => ({ ...p, client_snapshot: { ...p.client_snapshot, address: e.target.value } }))} placeholder="12 rue de la Paix" /></FormField>
              </div>
              <FormField label="Ville"><Input value={form.client_snapshot?.city || ''} onChange={e => setForm(p => ({ ...p, client_snapshot: { ...p.client_snapshot, city: e.target.value } }))} placeholder="Paris" /></FormField>
              <FormField label="Code postal"><Input value={form.client_snapshot?.zip_code || ''} onChange={e => setForm(p => ({ ...p, client_snapshot: { ...p.client_snapshot, zip_code: e.target.value } }))} placeholder="75001" /></FormField>
              <FormField label="Pays">
                <Select value={form.client_snapshot?.country || 'FR'} onChange={e => setForm(p => ({ ...p, client_snapshot: { ...p.client_snapshot, country: e.target.value } }))}>
                  {[['FR','France'],['BE','Belgique'],['CH','Suisse'],['LU','Luxembourg'],['DE','Allemagne'],['ES','Espagne'],['IT','Italie'],['GB','Royaume-Uni']].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                </Select>
              </FormField>
              <FormField label="SIRET"><Input value={form.client_snapshot?.siret || ''} onChange={e => setForm(p => ({ ...p, client_snapshot: { ...p.client_snapshot, siret: e.target.value } }))} placeholder="123 456 789 00012" /></FormField>
            </div>
          )}

          {/* Lignes */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>Lignes de facturation</div>
              <Button size="sm" variant="secondary" onClick={addLine}>+ Ajouter une ligne</Button>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--bg-page)' }}>
                  {['Description', 'Qté', 'P.U. HT (€)', 'Remise (%)', 'Total HT', ''].map(h => (
                    <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', borderBottom: '1px solid var(--border)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {form.items.map((line, i) => {
                  const lineTotal = (parseFloat(line.quantity) || 0) * (parseFloat(line.unit_price) || 0) * (1 - (parseFloat(line.discount) || 0) / 100);
                  return (
                    <tr key={i}>
                      <td style={{ padding: '6px 4px' }}><input className="form-input" value={line.description} onChange={e => updateLine(i, 'description', e.target.value)} placeholder="Description du service ou produit" style={{ fontSize: 13 }} /></td>
                      <td style={{ padding: '6px 4px', width: 70 }}><input className="form-input" type="number" min="1" value={line.quantity} onChange={e => updateLine(i, 'quantity', e.target.value)} style={{ fontSize: 13, textAlign: 'center' }} /></td>
                      <td style={{ padding: '6px 4px', width: 110 }}><input className="form-input" type="number" step="0.01" value={line.unit_price} onChange={e => updateLine(i, 'unit_price', e.target.value)} style={{ fontSize: 13, textAlign: 'right' }} /></td>
                      <td style={{ padding: '6px 4px', width: 80 }}><input className="form-input" type="number" min="0" max="100" value={line.discount} onChange={e => updateLine(i, 'discount', e.target.value)} style={{ fontSize: 13, textAlign: 'center' }} /></td>
                      <td style={{ padding: '6px 10px', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}>{lineTotal.toFixed(2)} €</td>
                      <td style={{ padding: '6px 4px' }}>
                        {form.items.length > 1 && <button onClick={() => removeLine(i)} style={{ color: 'var(--error)', fontSize: 18, cursor: 'pointer', background: 'none', border: 'none', lineHeight: 1 }}>×</button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Totaux + options */}
          <div style={{ display: 'flex', gap: 'var(--space-xl)', alignItems: 'flex-start' }}>
            <FormField label="Notes / Conditions" style={{ flex: 1 }}>
              <Textarea rows={3} value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Conditions de paiement, notes internes…" />
            </FormField>
            <div style={{ minWidth: 260, background: 'var(--bg-page)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 8 }}>
                <span>Sous-total HT</span><strong>{totals.subtotal.toFixed(2)} €</strong>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13, marginBottom: 8, gap: 8 }}>
                <span>Remise globale (%)</span>
                <input type="number" min="0" max="100" value={form.discount_pct} onChange={e => setForm(p => ({ ...p, discount_pct: e.target.value }))} style={{ width: 60, padding: '3px 6px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 12, textAlign: 'center' }} />
              </div>
              {totals.discount > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--success)', marginBottom: 8 }}><span>Remise</span><span>- {totals.discount.toFixed(2)} €</span></div>}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13, marginBottom: 12, gap: 8 }}>
                <span>TVA (%)</span>
                <input type="number" min="0" max="100" value={form.tax_rate} onChange={e => setForm(p => ({ ...p, tax_rate: e.target.value }))} style={{ width: 60, padding: '3px 6px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 12, textAlign: 'center' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 8, color: 'var(--text-muted)' }}>
                <span>TVA</span><span>{totals.tax.toFixed(2)} €</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 800, paddingTop: 8, borderTop: '2px solid var(--border)' }}>
                <span>Total TTC</span><span style={{ color: 'var(--brand-accent)' }}>{totals.total.toFixed(2)} €</span>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* ── Modal règlement ── */}
      <Modal isOpen={!!payModal} onClose={() => setPayModal(null)} title="Enregistrer un règlement"
        footer={<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}><Button variant="ghost" onClick={() => setPayModal(null)}>Annuler</Button><Button variant="primary" onClick={addPayment}>Enregistrer</Button></div>}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)' }}>
          <FormField label="Montant (€ TTC)" required hint={(() => { const inv = invoices.find(i => i.id === payModal); return inv ? `Solde restant : ${parseFloat(inv.balance_due||0).toFixed(2)} € — Total : ${parseFloat(inv.total_ttc||0).toFixed(2)} €` : ''; })()}>
            <Input type="number" step="0.01" value={payForm.amount} onChange={e => setPayForm(p => ({ ...p, amount: e.target.value }))} placeholder="0.00" />
          </FormField>
          <FormField label="Date du règlement">
            <Input type="date" value={payForm.paid_at} onChange={e => setPayForm(p => ({ ...p, paid_at: e.target.value }))} />
          </FormField>
          <FormField label="Mode de paiement">
            <Select value={payForm.method} onChange={e => setPayForm(p => ({ ...p, method: e.target.value }))}>
              {Object.entries(methods).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </Select>
          </FormField>
          <FormField label="Référence (optionnel)">
            <Input value={payForm.reference} onChange={e => setPayForm(p => ({ ...p, reference: e.target.value }))} placeholder="N° de chèque, virement…" />
          </FormField>
          <div style={{ gridColumn: '1/-1' }}>
            <FormField label="Notes">
              <Textarea rows={2} value={payForm.notes} onChange={e => setPayForm(p => ({ ...p, notes: e.target.value }))} />
            </FormField>
          </div>
        </div>
      </Modal>

      <ConfirmDialog isOpen={!!confirm} onClose={() => setConfirm(null)} onConfirm={() => del(confirm)} title="Supprimer la facture" message="Cette action est irréversible. La facture et ses règlements associés seront supprimés." confirmLabel="Supprimer définitivement" danger />
      <ConfirmDialog isOpen={bulkConfirmI} onClose={() => setBulkConfirmI(false)} onConfirm={bulkDelI} title="Supprimer la sélection" message={`${selIds.size} facture(s) seront définitivement supprimées.`} confirmLabel="Supprimer tout" danger />
    </div>
  );
};